import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import CustomerTrendKebabMenu from "./CustomerTrendKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import { mergeCustomerTrendModel } from "./dashboardChartsModel";
import "./dashboardChartJsSetup";
import "./CustomerTrendChartCard.scss";

const COLOR_NEW = "#F9BC44";
const COLOR_RETURN = "#5CC7C1";

/** @param {number | null | undefined} prev */
function dayOverDayPct(current, prev) {
  const c = typeof current === "number" ? current : Number(current);
  const p = typeof prev === "number" ? prev : Number(prev);
  if (!Number.isFinite(c)) return null;
  if (!Number.isFinite(p) || p === 0) return null;
  return ((c - p) / p) * 100;
}

function TrendPill({ pct }) {
  if (pct == null || !Number.isFinite(pct)) return null;
  const up = pct >= 0;
  const label = `${Math.abs(pct).toFixed(1)}%`;
  return (
    <span className={`pos-customer-trend-pill ${up ? "pos-customer-trend-pill--up" : "pos-customer-trend-pill--down"}`}>
      {up ? (
        <svg className="pos-customer-trend-pill__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M8 4l4 6H4l4-6z" fill="currentColor" />
        </svg>
      ) : (
        <svg className="pos-customer-trend-pill__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M8 12l-4-6h8l-4 6z" fill="currentColor" />
        </svg>
      )}
      <span>{label}</span>
    </span>
  );
}

/**
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CustomerTrendModel} props.model
 * @param {() => void} [props.onMenuRemove]
 */
function CustomerTrendChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(null);
  const [viewKind, setViewKind] = useState(() => /** @type {'chart'|'table'} */ ("chart"));

  const mergedModel = useMemo(() => mergeCustomerTrendModel(model && typeof model === "object" ? model : null), [model]);

  const labelNew = t("New Customer", { defaultValue: "New Customer" });
  const labelReturn = t("Return Customer", { defaultValue: "Return Customer" });

  const yMax = useMemo(() => {
    const nums = [...mergedModel.newCustomer, ...mergedModel.returnCustomer].filter((x) => Number.isFinite(x));
    const top = nums.length ? Math.max(...nums) : 0;
    return Math.max(50, Math.ceil(top / 10) * 10);
  }, [mergedModel.newCustomer, mergedModel.returnCustomer]);

  const chartData = useMemo(
    () => ({
      labels: mergedModel.labels,
      datasets: [
        {
          label: labelNew,
          data: mergedModel.newCustomer,
          borderColor: COLOR_NEW,
          backgroundColor: "transparent",
          borderWidth: 3,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: COLOR_NEW,
          pointBorderColor: COLOR_NEW,
        },
        {
          label: labelReturn,
          data: mergedModel.returnCustomer,
          borderColor: COLOR_RETURN,
          backgroundColor: "transparent",
          borderWidth: 3,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: COLOR_RETURN,
          pointBorderColor: COLOR_RETURN,
        },
      ],
    }),
    [mergedModel.labels, mergedModel.newCustomer, mergedModel.returnCustomer, labelNew, labelReturn],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#757575",
            font: { family: "'Segoe UI', 'Inter', Roboto, sans-serif", size: 10 },
            maxRotation: 0,
            autoSkip: true,
          },
          border: { display: false },
        },
        y: {
          min: 0,
          max: yMax,
          ticks: {
            stepSize: 10,
            color: "#111618",
            font: { family: "'Segoe UI', 'Inter', Roboto, sans-serif", size: 12 },
          },
          grid: { color: "#DDE2E4", drawTicks: false },
          border: { display: false },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            padding: 16,
            color: "#2A2A2A",
            font: { family: "'Inter', 'Segoe UI', Roboto, sans-serif", size: 12 },
          },
        },
        tooltip: {
          titleFont: { family: "'Segoe UI', 'Inter', Roboto, sans-serif", size: 12, weight: "600" },
          callbacks: {
            title: (items) => (items[0] ? String(items[0].label) : ""),
            label: (ctx) => {
              const i = ctx.dataIndex;
              const d0 = Number(ctx.chart.data.datasets[0].data[i]);
              const d1 = Number(ctx.chart.data.datasets[1].data[i]);
              const total = d0 + d1;
              const v = ctx.datasetIndex === 0 ? d0 : d1;
              const name = ctx.datasetIndex === 0 ? labelNew : labelReturn;
              const pct = total > 0 ? ((v / total) * 100).toFixed(2) : "0.00";
              return `${name}: ${v} (${pct}%)`;
            },
          },
        },
      },
    }),
    [yMax, labelNew, labelReturn],
  );

  const tableRows = useMemo(() => {
    const { labels, newCustomer, returnCustomer } = mergedModel;
    return labels.map((date, i) => ({
      date,
      newVal: newCustomer[i],
      retVal: returnCustomer[i],
      newPct: dayOverDayPct(newCustomer[i], i > 0 ? newCustomer[i - 1] : null),
      retPct: dayOverDayPct(returnCustomer[i], i > 0 ? returnCustomer[i - 1] : null),
    }));
  }, [mergedModel]);

  const [sort, setSort] = useState(() => ({
    key: /** @type {"date"|"new"|"ret"} */ ("date"),
    dir: /** @type {"asc"|"desc"} */ ("asc"),
  }));

  useEffect(() => {
    setSort({ key: "date", dir: "asc" });
  }, [mergedModel]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const sortedTableRows = useMemo(() => {
    const copy = tableRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "date":
          c = String(a.date).localeCompare(String(b.date), undefined, { sensitivity: "base" });
          break;
        case "new":
          c = (Number(a.newVal) || 0) - (Number(b.newVal) || 0);
          break;
        case "ret":
          c = (Number(a.retVal) || 0) - (Number(b.retVal) || 0);
          break;
        default:
          c = 0;
      }
      return c * mul;
    });
    return copy;
  }, [tableRows, sort]);

  const trendThBase = "pos-customer-trend-table__th";
  const trendThSorted = "pos-customer-trend-table__th--sorted";

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("customer-trend"));
  }, []);

  return (
    <div className="pos-dashboard-chart-card pos-dashboard-chart-card--customer-trend">
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">
          {t("graphCustomerTrend", { defaultValue: "Customer trend" })}
        </h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <CustomerTrendKebabMenu
            viewKind={viewKind}
            onViewKindChange={setViewKind}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body ${viewKind === "table" ? "pos-dashboard-chart-card__body--table" : ""}`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture">
          {viewKind === "chart" ? (
            <div className="pos-customer-trend-line">
              <div className="pos-customer-trend-line__canvas">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          ) : (
            <div className="pos-customer-trend-table" role="table" aria-label={t("Customer trend table", { defaultValue: "Customer trend table" })}>
              <div className="pos-customer-trend-table__head">
                <div className="pos-customer-trend-table__tr pos-customer-trend-table__tr--head" role="row">
                  <DashboardChartTableSortHeader
                    baseClassName={trendThBase}
                    sortedClassName={trendThSorted}
                    className="pos-customer-trend-table__th--date"
                    ariaSortLabel={t("Sort by date", { defaultValue: "Sort by date" })}
                    sorted={sort.key === "date"}
                    direction={sort.dir}
                    onSort={() => toggleSort("date")}
                  >
                    {t("Date", { defaultValue: "Date" })}
                  </DashboardChartTableSortHeader>
                  <DashboardChartTableSortHeader
                    baseClassName={trendThBase}
                    sortedClassName={trendThSorted}
                    className="pos-customer-trend-table__th--num"
                    ariaSortLabel={t("Sort by new customers", { defaultValue: "Sort by new customers" })}
                    sorted={sort.key === "new"}
                    direction={sort.dir}
                    onSort={() => toggleSort("new")}
                  >
                    {labelNew}
                  </DashboardChartTableSortHeader>
                  <DashboardChartTableSortHeader
                    baseClassName={trendThBase}
                    sortedClassName={trendThSorted}
                    className="pos-customer-trend-table__th--num pos-customer-trend-table__th--last"
                    ariaSortLabel={t("Sort by return customers", { defaultValue: "Sort by return customers" })}
                    sorted={sort.key === "ret"}
                    direction={sort.dir}
                    onSort={() => toggleSort("ret")}
                  >
                    {labelReturn}
                  </DashboardChartTableSortHeader>
                </div>
              </div>
              <div className="pos-customer-trend-table__scroll">
                {sortedTableRows.map((row) => (
                  <div key={row.date} className="pos-customer-trend-table__tr" role="row">
                    <div className="pos-customer-trend-table__td pos-customer-trend-table__td--date" role="cell">
                      {row.date}
                    </div>
                    <div className="pos-customer-trend-table__td pos-customer-trend-table__td--metric" role="cell">
                      <span className="pos-customer-trend-table__metric-val">{row.newVal}</span>
                      <TrendPill pct={row.newPct} />
                    </div>
                    <div className="pos-customer-trend-table__td pos-customer-trend-table__td--metric" role="cell">
                      <span className="pos-customer-trend-table__metric-val">{row.retVal}</span>
                      <TrendPill pct={row.retPct} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomerTrendChartCard;
